import { useMutation, useQueryClient } from '@tanstack/react-query';
import { businessAccessApi } from '../api/businessAccessClient';
import type { RefundPaymentResponse } from '../types/payment.types';
import { RefundConfirmation, RefundResponseError } from '../utils/refunds';

/**
 * SAM-466: POST /business_access/refund for one confirmation.
 *
 * The idempotency key travels in the body (`idempotency_key`), which refunds.py accepts exactly
 * like the `Idempotency-Key` header. The header is not used from the browser because the
 * business_access CORS preflight only allows Content-Type, X-Amz-Date, Authorization, X-Api-Key
 * and X-Amz-Security-Token: a custom header would make the browser refuse the request before it
 * is sent. The request fingerprint on the server does not include the key, so both are equivalent.
 */
export const postRefund = async (confirmation: RefundConfirmation): Promise<RefundPaymentResponse> => {
  const response = await businessAccessApi<RefundPaymentResponse>({
    url: '/refund',
    method: 'POST',
    data: { ...confirmation.request, idempotency_key: confirmation.idempotencyKey },
    skipGlobalErrorHandling: true,
  });
  if (!response || typeof response !== 'object' || !(response as RefundPaymentResponse).refund) {
    throw new RefundResponseError('Refund response without a refund');
  }
  return response;
};

export const useRefundPayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postRefund,
    // Success or failure, the ledger may have changed (a failure can still leave a reserved or
    // unknown row), so the list is refreshed either way.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['business-payments'] });
    },
  });
};
